import React, { useState } from 'react';

const ReverseText: React.FC = () => {
  const [text, setText] = useState('');
  const [reverseType, setReverseType] = useState('entire');

  const reverseText = () => {
    if (!text) return '';

    switch (reverseType) {
      case 'entire':
        return text.split('').reverse().join('');
      
      case 'words':
        return text.split(' ').reverse().join(' ');
      
      case 'lines':
        return text.split('\n').reverse().join('\n');
      
      case 'each-word':
        return text.split(' ').map(word => 
          word.split('').reverse().join('')
        ).join(' ');
      
      case 'each-line':
        return text.split('\n').map(line => 
          line.split('').reverse().join('')
        ).join('\n');
      
      case 'sentences':
        return text.split(/([.!?]+)/).filter(Boolean).reverse().join('');
      
      default:
        return text;
    }
  };

  const reversedText = reverseText();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(reversedText);
  };

  const clearText = () => {
    setText('');
  };

  const reverseOptions = [
    { value: 'entire', label: 'Entire Text', description: 'Reverse all characters in the text' },
    { value: 'words', label: 'Word Order', description: 'Reverse the order of words' },
    { value: 'lines', label: 'Line Order', description: 'Reverse the order of lines' },
    { value: 'each-word', label: 'Each Word', description: 'Reverse characters in each word individually' },
    { value: 'each-line', label: 'Each Line', description: 'Reverse characters in each line individually' },
    { value: 'sentences', label: 'Sentence Order', description: 'Reverse the order of sentences' }
  ];

  const getExampleText = () => {
    const examples = {
      'entire': 'Hello World → dlroW olleH',
      'words': 'Hello World → World Hello',
      'lines': 'Line 1\\nLine 2 → Line 2\\nLine 1',
      'each-word': 'Hello World → olleH dlroW',
      'each-line': 'Hello\\nWorld → olleH\\ndlroW',
      'sentences': 'Hello. World! → World! Hello.'
    };
    return examples[reverseType as keyof typeof examples] || '';
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Reverse Text</h1>
        <p className="text-gray-600">Reverse text in various ways - characters, words, lines, and more</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Enter Your Text</h3>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type or paste your text here..."
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <div className="mt-4 flex gap-2">
              <button 
                onClick={clearText}
                disabled={!text}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
              >
                Clear Text
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Reverse Options</h3>
            <div className="space-y-3">
              {reverseOptions.map(option => (
                <label key={option.value} className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    name="reverseType"
                    value={option.value}
                    checked={reverseType === option.value}
                    onChange={(e) => setReverseType(e.target.value)}
                    className="mt-1 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-700">{option.label}</span>
                    <p className="text-xs text-gray-500">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
            
            <div className="mt-6 p-3 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Example</h4>
              <p className="text-xs text-gray-600 font-mono">{getExampleText()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Reversed Text</h3>
              <button
                onClick={copyToClipboard}
                disabled={!reversedText}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 transition-colors"
              >
                Copy Result
              </button>
            </div>
            <textarea
              value={reversedText}
              readOnly
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50"
              placeholder="Reversed text will appear here..."
            />
            
            {text && (
              <div className="mt-4 text-sm text-gray-600">
                <p>Original length: {text.length} characters</p>
                <p>Reversed length: {reversedText.length} characters</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReverseText;
